import { useEffect, useMemo, useRef, useState } from 'react';

import {
  appDelay,
  formatMoney,
  Buyer,
  DocumentsSummary,
  calculateStripePartialFacilitationFeesAndAmount,
  DillPaymentMethod,
  capitalizeWordsInSentence,
  calculateFiservFee,
  FiservCardDetails,
  getBuyerFiservFeeRate,
} from '@dill/dill-shared';
import { DillButton, DillCheckbox, DillModal } from '../../../general/Dill';
import { COLORS } from '../../../../utils/colors';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type { StripeCardElementChangeEvent } from '@stripe/stripe-js';
import {
  deleteSupplierCustomerPaymentMethod,
  getSupplierCustomerPaymentMethods,
  supplierFiservPayWillCallInvoices,
  supplierPayWillCallInvoices,
} from '../../../../redux/services/paymentsService';
import { closeMessageModal, openMessageModal } from '../../../../redux/globalSlices/genericSlice';
import { twMerge } from 'tailwind-merge';
import { AppDropDown2 } from '../../../general/AppDropDown2/AppDropDown2';
import AppRangeSlider from '../../../general/AppAppRangeSlider/AppRangeSlider';
import { AppRadioButton } from '../../../general/AppRadioButton/AppRadioButton';
import { AppInputField } from '../../../general/AppInputField/AppInputField';
import {
  getUserCustomers,
  recursiveGetUserCustomers,
} from '../../../../redux/services/suppliersService';
import { Bank, CreditCard, Spinner, Trash } from 'phosphor-react';
import AppFiservCardInput, {
  FiservCardResult,
} from '../../../general/AppFiservCardInput/AppFiservCardInput';
import moment from 'moment';
import { useFIFOEffect } from '../../../../utils/hooks/useFIFOEffect';
import { useIdempotencyKey } from '../../../../utils/hooks/useIdempotencyKey';
import { IDEMPOTENT_REPLAY_SUBTITLE, IDEMPOTENT_REPLAY_TITLE } from '../../../../utils/idempotency';

export const SupplierAddWillCallPaymentModal = ({
  open,
  title = 'Add Will Call Payment',
  handleClose = () => {},
  handlePaymentDone = () => {},
}: {
  open: boolean;
  title?: string;
  handleClose: () => void;
  handlePaymentDone: () => void;
}) => {
  const dispatch = useAppDispatch();
  const { user, firebaseUserId } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  // Tracks the active supplier id for in-flight customer fetches, so a chain started for a
  // previous supplier stops once the supplier is switched (otherwise its results would be
  // written into the now differently-scoped store). The request ref lets us abort in-flight.
  const activeFetchSupplierIdRef = useRef<string | null>(currentSupplier?.id ?? null);
  const inFlightCustomersRequestRef = useRef<{ abort: () => void } | null>(null);
  useEffect(() => {
    activeFetchSupplierIdRef.current = currentSupplier?.id ?? null;
  }, [currentSupplier?.id]);
  useEffect(() => () => inFlightCustomersRequestRef.current?.abort(), [currentSupplier?.id]);
  const stripe: any = useStripe();
  const elements: any = useElements();
  const [cardStatus, setCardStatus] = useState({ isCardComplete: false, errorMessage: '' });
  const [isShowErrors, setIsShowErrors] = useState(false);
  const [previousLastDocumentId, setPreviousLastDocumentId] = useState('');
  const [loading, setLoading] = useState<boolean>(false);
  const [customerCount, setCustomerCount] = useState(0);
  const mainRoles = user?.roles.filter((role) => role !== 'DILL_ADMIN');

  const [whoPaysFee, setWhoPaysFee] = useState<'SUPPLIER' | 'BUYER' | 'BOTH'>('SUPPLIER');

  const { customers } = useAppSelector((state) => state.supplier);

  const [selectedCustomer, setSelectedCustomer] = useState<{
    buyer: Buyer;
    usersEmailsDetails: {
      email: string;
      isStatementsEmailOptOut: boolean;
      isPaymentRemindersEmailOptOut: boolean;
      isInvitedUser: boolean;
    }[];
    documentsSummary: DocumentsSummary;
  } | null>(null);

  const [customerEmails, setCustomerEmails] = useState<string[]>([]);
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [supplierTakeRate, setSupplierTakeRate] = useState(0);
  const [buyerTakeRate, setBuyerTakeRate] = useState(0);
  const [paymentAmount, setPaymentAmount] = useState<number | null>(null);
  const [hasBadAmountInput, setHasBadAmountInput] = useState(false);
  const [isSaveCard, setIsSaveCard] = useState(false);
  const [isFetchingSaveCards, setIsFetchingSaveCards] = useState(false);
  const [savedCards, setSavedCards] = useState<DillPaymentMethod[]>([]);
  const [selectedCard, setSelectedCard] = useState<DillPaymentMethod | 'NEW_CARD'>('NEW_CARD');
  const [paymentType, setPaymentType] = useState('creditCard');
  const [verifiedFiservCardData, setVerifiedFiservCardData] = useState<FiservCardDetails | null>(
    null
  );

  const { resolveKey: resolveIdempotencyKey, clearKey: clearIdempotencyKey } = useIdempotencyKey();

  /**
   * Mirrors what the backend fingerprints. The card token and the per-attempt
   * Stripe/Fiserv payment method id are deliberately absent: they are minted
   * fresh on every submit, so including them would make each retry look like a
   * different payment.
   */
  const buildWillCallSignature = (provider: 'STRIPE' | 'FISERV') => ({
    provider,
    supplierId: currentSupplier?.id || '',
    buyerId: selectedCustomer?.buyer.id || '',
    paymentAmount,
    whoPaysFee,
    buyerTakeRate,
    notes,
    email: selectedCard === 'NEW_CARD' ? email : undefined,
    selectedDillPaymentMethodId: selectedCard !== 'NEW_CARD' ? selectedCard.id : undefined,
  });

  const fetchedSupplierBuyers = useMemo(() => {
    if (customers.length > 0) {
      return customers;
    }
    return [];
  }, [customers]);
  useEffect(() => {
    if (currentSupplier) {
      setSupplierTakeRate(currentSupplier?.creditCardTakeRate || 3.5);
    }
    return () => {};
  }, [currentSupplier]);

  useFIFOEffect(async () => {
    if (
      (selectedCard && selectedCard !== 'NEW_CARD' && selectedCard.cardType === 'CREDIT') ||
      (selectedCard === 'NEW_CARD' && paymentType === 'creditCard')
    ) {
      if (currentSupplier?.cardPaymentProvider === 'FISERV') {
        await appDelay(100);
        setBuyerTakeRate(
          getBuyerFiservFeeRate({
            buyer: selectedCustomer?.buyer,
            supplier: currentSupplier,
          })
        );
      }
    }
  }, [selectedCustomer]);

  useEffect(() => {
    if (selectedCustomer) {
      setCustomerEmails([
        ...(selectedCustomer.usersEmailsDetails.map((email1) => email1.email) || []),
      ]);
    }
  }, [selectedCustomer]);

  useEffect(() => {
    if (buyerTakeRate === 0 || paymentType !== 'creditCard') {
      setWhoPaysFee('SUPPLIER');
      setBuyerTakeRate(0);
    } else if (buyerTakeRate === supplierTakeRate && paymentType === 'creditCard') {
      setWhoPaysFee('BUYER');
    } else if (paymentType === 'creditCard') {
      setWhoPaysFee('BOTH');
    }

    return () => {};
  }, [buyerTakeRate, paymentType]);

  useEffect(() => {
    if (open) {
      setIsShowErrors(false);
      setCardStatus({ isCardComplete: false, errorMessage: '' });
      setIsShowErrors(false);
      setSelectedCustomer(null);
      setEmail('');
      setPaymentAmount(null);
      setHasBadAmountInput(false);
      setNotes('');
      setSavedCards([]);
      setBuyerTakeRate(0);
      setIsSaveCard(false);
      setSelectedCard('NEW_CARD');
    }

    if (!open) {
      setIsShowErrors(false);
      setSelectedCustomer(null);
      setEmail('');
      setPaymentAmount(null);
      setHasBadAmountInput(false);
      setNotes('');
    }

    return () => {};
  }, [open]);

  const handleCardChange = (event: StripeCardElementChangeEvent) => {
    setCardStatus({
      isCardComplete: event.complete,
      errorMessage: !event.complete ? event.error?.message || '' : '',
    });
  };

  const { buyerFee } = useMemo(() => {
    if (
      currentSupplier &&
      (currentSupplier?.cardPaymentProvider === 'STRIPE' ||
        !currentSupplier?.cardPaymentProvider) &&
      whoPaysFee &&
      paymentAmount &&
      selectedCustomer
    ) {
      const supplier = currentSupplier;
      const amount = paymentAmount;
      const calculateAmountFee = calculateStripePartialFacilitationFeesAndAmount({
        amount,
        supplier: { ...supplier, creditCardTakeRateExtra: 0 },
        buyer: {
          ...selectedCustomer.buyer,
          whoPaysCreditCardFee: whoPaysFee,
          ...(buyerTakeRate && whoPaysFee === 'BOTH'
            ? {
                creditCardTakeRate: buyerTakeRate,
                creditCardTakeRateExtra: undefined,
              }
            : { creditCardTakeRate: undefined, creditCardTakeRateExtra: undefined }),
        },
      });
      return { buyerFee: calculateAmountFee.buyerFees };
    }

    if (
      currentSupplier &&
      currentSupplier?.cardPaymentProvider === 'FISERV' &&
      whoPaysFee &&
      paymentAmount &&
      selectedCustomer
    ) {
      const amount = paymentAmount;
      const calculateAmountFee = calculateFiservFee({
        amount,
        percentageRate: buyerTakeRate,
      });
      return { buyerFee: calculateAmountFee.buyerFees };
    }

    return { buyerFee: 0 };
  }, [paymentAmount, whoPaysFee, selectedCustomer, buyerTakeRate]);

  const checkForErrors = () => {
    let isError = false;

    if (
      selectedCard === 'NEW_CARD' &&
      (currentSupplier?.cardPaymentProvider === 'STRIPE' ||
        !currentSupplier?.cardPaymentProvider) &&
      !cardStatus.isCardComplete
    ) {
      isError = true;
    }
    if (
      selectedCard === 'NEW_CARD' &&
      currentSupplier?.cardPaymentProvider === 'FISERV' &&
      !verifiedFiservCardData
    ) {
      isError = true;
    }
    if (!selectedCustomer) {
      isError = true;
    }
    if (!paymentAmount) {
      isError = true;
    }
    if (selectedCard === 'NEW_CARD' && !email) {
      isError = true;
    }

    if (!notes) {
      isError = true;
    }
    return isError;
  };

  const recursiveFetchCustomers = async (
    count: number,
    lastDockId: string,
    firstCustomers: {
      buyer: Buyer;
      usersEmailsDetails: {
        email: string;
        userId: string;
        isInvitedUser: boolean;
        isStatementsEmailOptOut: boolean;
        isPaymentRemindersEmailOptOut: boolean;
      }[];
      documentsSummary: DocumentsSummary;
    }[],
    supplierId: string
  ) => {
    if (firstCustomers.length > count) {
      return;
    }
    setCustomerCount(count);
    const pageSize = 100;
    let currentPreviousLastDocumentId = lastDockId;
    for (let i = firstCustomers.length; i < count; i += pageSize) {
      // A newer fetch (e.g. after switching suppliers) supersedes this loop — stop so we
      // don't write the previous supplier's customers into the store.
      if (!supplierId || activeFetchSupplierIdRef.current !== supplierId) {
        break;
      }
      const request = dispatch(
        recursiveGetUserCustomers({
          supplierId,
          previousLastDocumentId: currentPreviousLastDocumentId,
          pageSize,
          sortBy: 'name',
          sortOrder: 'asc',
        })
      );
      inFlightCustomersRequestRef.current = request;
      const results = await request;
      if (activeFetchSupplierIdRef.current !== supplierId) {
        break;
      }
      if (results.type === 'supplier/recursiveGetUserCustomers/fulfilled') {
        if (results.payload) {
          const payload = results.payload as any;
          const fetchedCustomers: {
            buyer: Buyer;
            usersEmailsDetails: {
              email: string;
              isInvitedUser: boolean;
              isStatementsEmailOptOut: boolean;
              isPaymentRemindersEmailOptOut: boolean;
            }[];
            documentsSummary: DocumentsSummary;
          }[] = payload.data as any;
          const lastDocumentId = payload.lastDocumentId;
          if (lastDocumentId !== currentPreviousLastDocumentId) {
            setPreviousLastDocumentId(lastDocumentId);
            currentPreviousLastDocumentId = lastDocumentId;
          }
          if (!lastDockId || fetchedCustomers.length < pageSize) {
            console.log('No more pages to fetch');
            break;
          }
        }
      } else {
        console.log('Fetch failed or interrupted');
        break;
      }
    }
  };

  const handleLoadMore = async (loadOptions?: { isFreshFetch?: boolean }) => {
    const fetchSupplierId = currentSupplier?.id || '';
    if (!fetchSupplierId) {
      return;
    }
    setLoading(true);
    const pageSize = 100;
    let currentPreviousLastDocumentId = previousLastDocumentId;
    if (loadOptions && loadOptions?.isFreshFetch) {
      currentPreviousLastDocumentId = '';
    }
    const request = dispatch(
      getUserCustomers({
        supplierId: fetchSupplierId,
        previousLastDocumentId: currentPreviousLastDocumentId,
        pageSize,
        sortBy: 'name',
        sortOrder: 'asc',
      })
    );
    inFlightCustomersRequestRef.current = request;
    const results = await request;
    setLoading(false);
    if (activeFetchSupplierIdRef.current !== fetchSupplierId) {
      return;
    }
    if (results.type === 'supplier/getUserCustomers/fulfilled') {
      if (results.payload) {
        const payload = results.payload as any;
        const cust: {
          buyer: Buyer;
          usersEmailsDetails: {
            email: string;
            userId: string;
            isInvitedUser: boolean;
            isStatementsEmailOptOut: boolean;
            isPaymentRemindersEmailOptOut: boolean;
          }[];
          documentsSummary: DocumentsSummary;
        }[] = payload.data as any;
        const lastDockId = payload.lastDocumentId;
        if (!customerCount && payload.customerCount) {
          setCustomerCount(payload.customerCount || 0);
        }
        if (lastDockId && lastDockId !== currentPreviousLastDocumentId) {
          setPreviousLastDocumentId(lastDockId);

          const count = Number(payload.customerCount);
          recursiveFetchCustomers(count, lastDockId, [...cust], fetchSupplierId);
        }
      }
    }
  };

  useEffect(() => {
    if (
      mainRoles?.includes('SUPPLIER') &&
      firebaseUserId &&
      customers.length === 0 &&
      !previousLastDocumentId &&
      !loading
    ) {
      setPreviousLastDocumentId('');
      handleLoadMore({ isFreshFetch: true });
    }

    return () => {};
  }, [user?.id, firebaseUserId, location.pathname]);

  const handlePay = async () => {
    setIsShowErrors(true);
    if (selectedCard === 'NEW_CARD' && (!stripe || !elements)) {
      return;
    }
    if (checkForErrors()) {
      return;
    }

    if (paymentAmount === 0 || !paymentAmount) {
      dispatch(
        openMessageModal({
          buttonText: 'Close',
          modalType: 'ERROR',
          title: 'Amount must be grater than $0',
          subTitle: '',
        })
      );
      return;
    }

    dispatch(
      openMessageModal({
        buttonText: 'Cancel',
        isSecondButton: true,
        secondButtonText: 'Pay Now',
        modalType: 'WARN',
        title: `Confirm payment below`,
        subTitle: 'The submitted card details will be charged the given amount.',

        onSecondButtonClick: async () => {
          await appDelay(10);
          dispatch(
            openMessageModal({
              buttonText: '',
              modalType: 'LOADING',
              title: 'Processing payment',
              subTitle: 'This may take a few minutes. Please do not refresh the page',
            })
          );
          let senderStripePaymentMethodId = '';
          if (selectedCard === 'NEW_CARD') {
            const cardElement = elements.getElement(CardElement);

            const { error, paymentMethod } = await stripe.createPaymentMethod({
              type: 'card',
              card: cardElement,
            });
            if (error) {
              dispatch(
                openMessageModal({
                  buttonText: 'Close',
                  modalType: 'ERROR',
                  title: 'Error validating card',
                  subTitle: `${error}`,
                })
              );
              return;
            }
            if (
              paymentType === 'creditCard' &&
              paymentMethod.card?.funding &&
              paymentMethod.card?.funding.toUpperCase() !== 'CREDIT'
            ) {
              dispatch(
                openMessageModal({
                  buttonText: 'Close',
                  modalType: 'ERROR',
                  title: 'Wrong Card',
                  subTitle:
                    'The card entered is not a credit card. Please change your payment method selection to Debit / Other Card.',
                })
              );
              return;
            }
            if (
              paymentType === 'debitOrOtherCard' &&
              paymentMethod.card?.funding &&
              paymentMethod.card?.funding.toUpperCase() === 'CREDIT'
            ) {
              dispatch(
                openMessageModal({
                  buttonText: 'Close',
                  modalType: 'ERROR',
                  title: 'Wrong Card',
                  subTitle:
                    'The card entered is a credit card. Please change your payment method selection to Credit Card.',
                })
              );

              return;
            }
            senderStripePaymentMethodId = paymentMethod.id;
          } else {
            senderStripePaymentMethodId = selectedCard.providerPaymentMethodId;
          }

          const paymentResponse = await dispatch(
            supplierPayWillCallInvoices({
              supplierId: currentSupplier?.id || '',
              idempotencyKey: resolveIdempotencyKey(buildWillCallSignature('STRIPE')),
              details: {
                isSaveCard,
                whoPaysFee,
                email: selectedCard === 'NEW_CARD' ? email : undefined,
                selectedDillpaymentMethodId:
                  selectedCard !== 'NEW_CARD' ? selectedCard.id : undefined,
                notes,
                paymentAmount,
                buyerTakeRate,
                buyerId: selectedCustomer?.buyer.id || '',
                receivingSupplierId: currentSupplier?.id || '',
                senderStripePaymentMethodId: senderStripePaymentMethodId,
              },
            })
          );

          if (paymentResponse.type === 'payments/supplierPayWillCallInvoices/fulfilled') {
            const dataResults: any = paymentResponse.payload;
            if (dataResults && dataResults?.data?.data?.clientSecret) {
              const clientSecret = dataResults?.data?.data?.clientSecret;
              try {
                const stripeConfirmRes = await stripe?.confirmCardPayment(clientSecret, {});
                if (stripeConfirmRes?.error) {
                  dispatch(
                    openMessageModal({
                      buttonText: 'Close',
                      modalType: 'ERROR',
                      title: 'Error processing payment',
                      subTitle: `${stripeConfirmRes?.error?.message || ''}`,
                    })
                  );
                  return;
                }
              } catch (err) {
                dispatch(
                  openMessageModal({
                    buttonText: 'Close',
                    modalType: 'ERROR',
                    title: 'Error processing payment',
                    subTitle: 'Error validating payment. Please try again',
                  })
                );
                return;
              }
            }
            // Payment landed — retire the key so the next one starts fresh. Placed
            // after the card-confirmation branches above: while one is unresolved
            // the key must stay, so a retry replays rather than charging again.
            clearIdempotencyKey();
            const isReplay = (paymentResponse.payload as any)?.isReplay === true;
            dispatch(closeMessageModal());
            dispatch(
              openMessageModal({
                buttonText: 'Close',
                modalType: 'SUCCESS',
                title: isReplay ? IDEMPOTENT_REPLAY_TITLE : 'Payment successfully sent!',
                subTitle: isReplay
                  ? IDEMPOTENT_REPLAY_SUBTITLE
                  : `Please check your Transaction History for confirmation of final payment status`,
                onClose: () => {
                  handlePaymentDone();
                },
              })
            );
          } else if (paymentResponse.type === 'payments/supplierPayWillCallInvoices/rejected') {
            dispatch(closeMessageModal());
            let errorMessage = '';
            if (paymentResponse.type === 'payments/supplierPayWillCallInvoices/rejected') {
              errorMessage = paymentResponse.payload as any;
            }
            dispatch(
              openMessageModal({
                buttonText: 'Close',
                modalType: 'ERROR',
                title: 'Error processing payment',
                subTitle: `Contact customer support. ${errorMessage}`,
              })
            );
          } else {
            dispatch(closeMessageModal());
          }
        },
      })
    );
  };
  const handleFiservPay = async () => {
    setIsShowErrors(true);
    if (selectedCard === 'NEW_CARD' && !verifiedFiservCardData) {
      return;
    }

    if (checkForErrors()) {
      return;
    }

    if (paymentAmount === 0 || !paymentAmount) {
      dispatch(
        openMessageModal({
          buttonText: 'Close',
          modalType: 'ERROR',
          title: 'Amount must be grater than $0',
          subTitle: '',
        })
      );
      return;
    }

    dispatch(
      openMessageModal({
        buttonText: 'Cancel',
        isSecondButton: true,
        secondButtonText: 'Pay Now',
        modalType: 'WARN',
        title: `Confirm payment below`,
        subTitle: 'The submitted card details will be charged the given amount.',

        onSecondButtonClick: async () => {
          await appDelay(10);
          dispatch(
            openMessageModal({
              buttonText: '',
              modalType: 'LOADING',
              title: 'Processing payment',
              subTitle: 'This may take a few minutes. Please do not refresh the page',
            })
          );
          let senderFiservPaymentMethodId = '';
          if (selectedCard === 'NEW_CARD' && verifiedFiservCardData) {
            if (
              paymentType === 'creditCard' &&
              verifiedFiservCardData.cardType.toUpperCase() !== 'CREDIT'
            ) {
              dispatch(
                openMessageModal({
                  buttonText: 'Close',
                  modalType: 'ERROR',
                  title: 'Wrong Card',
                  subTitle:
                    'The card entered is not a credit card. Please change your payment method selection to Debit / Other Card.',
                })
              );
              return;
            }
            if (
              paymentType === 'debitOrOtherCard' &&
              verifiedFiservCardData.cardType.toUpperCase() === 'CREDIT'
            ) {
              dispatch(
                openMessageModal({
                  buttonText: 'Close',
                  modalType: 'ERROR',
                  title: 'Wrong Card',
                  subTitle:
                    'The card entered is a credit card. Please change your payment method selection to Credit Card.',
                })
              );

              return;
            }
            senderFiservPaymentMethodId = verifiedFiservCardData?.token || '';
          } else if (selectedCard !== 'NEW_CARD') {
            senderFiservPaymentMethodId = selectedCard.providerPaymentMethodId;
          }

          const paymentResponse = await dispatch(
            supplierFiservPayWillCallInvoices({
              supplierId: currentSupplier?.id || '',
              idempotencyKey: resolveIdempotencyKey(buildWillCallSignature('FISERV')),
              details: {
                isSaveCard,
                whoPaysFee,
                email: selectedCard === 'NEW_CARD' ? email : undefined,
                selectedDillPaymentMethodId:
                  selectedCard !== 'NEW_CARD' ? selectedCard.id : undefined,
                notes,
                paymentAmount,
                buyerTakeRate,
                buyerId: selectedCustomer?.buyer.id || '',
                receivingSupplierId: currentSupplier?.id || '',
                senderFiservPaymentMethodId: senderFiservPaymentMethodId,
                fiservCardDetails: verifiedFiservCardData,
              },
            })
          );

          if (paymentResponse.type === 'payments/supplierFiservPayWillCallInvoices/fulfilled') {
            const dataResults: any = paymentResponse.payload;
            if (dataResults && dataResults?.data?.data?.clientSecret) {
              const clientSecret = dataResults?.data?.data?.clientSecret;
              try {
                const stripeConfirmRes = await stripe?.confirmCardPayment(clientSecret, {});
                if (stripeConfirmRes?.error) {
                  dispatch(
                    openMessageModal({
                      buttonText: 'Close',
                      modalType: 'ERROR',
                      title: 'Error processing payment',
                      subTitle: `${stripeConfirmRes?.error?.message || ''}`,
                    })
                  );
                  return;
                }
              } catch (err) {
                dispatch(
                  openMessageModal({
                    buttonText: 'Close',
                    modalType: 'ERROR',
                    title: 'Error processing payment',
                    subTitle: 'Error validating payment. Please try again',
                  })
                );
                return;
              }
            }
            // Payment landed — retire the key so the next one starts fresh. Placed
            // after the card-confirmation branches above: while one is unresolved
            // the key must stay, so a retry replays rather than charging again.
            clearIdempotencyKey();
            const isReplay = (paymentResponse.payload as any)?.isReplay === true;
            dispatch(closeMessageModal());
            dispatch(
              openMessageModal({
                buttonText: 'Close',
                modalType: 'SUCCESS',
                title: isReplay ? IDEMPOTENT_REPLAY_TITLE : 'Payment successfully sent!',
                subTitle: isReplay
                  ? IDEMPOTENT_REPLAY_SUBTITLE
                  : `Please check your Transaction History for confirmation of final payment status`,
                onClose: () => {
                  handlePaymentDone();
                },
              })
            );
          } else if (
            paymentResponse.type === 'payments/supplierFiservPayWillCallInvoices/rejected'
          ) {
            dispatch(closeMessageModal());
            let errorMessage = '';
            if (paymentResponse.type === 'payments/supplierFiservPayWillCallInvoices/rejected') {
              errorMessage = paymentResponse.payload as any;
            }
            dispatch(
              openMessageModal({
                buttonText: 'Close',
                modalType: 'ERROR',
                title: 'Error processing payment',
                subTitle: `Contact customer support. ${errorMessage}`,
              })
            );
          } else {
            dispatch(closeMessageModal());
          }
        },
      })
    );
  };

  const getSavedCards = async () => {
    setSavedCards([]);
    setIsFetchingSaveCards(true);
    const savedCardsRes = await dispatch(
      getSupplierCustomerPaymentMethods({
        customerId: selectedCustomer?.buyer.id || '',
        supplierId: currentSupplier?.id || '',
        isHideLoadingIndicator: true,
      })
    );
    setIsFetchingSaveCards(false);
    if (savedCardsRes.type === 'payments/getSupplierCustomerPaymentMethods/fulfilled') {
      if (savedCardsRes.payload) {
        const payload = savedCardsRes.payload as any;
        setSavedCards(payload.data);
      }
    } else {
    }
  };
  useEffect(() => {
    if (currentSupplier?.id && selectedCustomer?.buyer.id) {
      getSavedCards();
    }
    if (!selectedCustomer) {
      setSavedCards([]);
    }
    return () => {};
  }, [selectedCustomer, currentSupplier?.id]);

  const paymentOptions = useMemo(() => {
    let options: { id: string; name: string }[] = [];
    const cardOptions = [
      { id: 'creditCard', name: 'Credit Card' },
      { id: 'debitOrOtherCard', name: 'Debit / Other Card' },
    ];
    options = [...cardOptions];

    return options;
  }, []);

  const deleteCustomerPaymentMethod = async ({ paymentMethodId }: { paymentMethodId: string }) => {
    if (!selectedCustomer) {
      return;
    }
    dispatch(
      openMessageModal({
        buttonText: 'Cancel',
        isSecondButton: true,
        secondButtonText: 'Delete',
        modalType: 'WARN',
        title: `Are you sure you want to delete this payment method?`,
        subTitle: 'This action can not be undone.',
        onSecondButtonClick: async () => {
          await appDelay(10);
          dispatch(
            openMessageModal({
              buttonText: '',
              modalType: 'LOADING',
              title: 'Deleting',
              subTitle: 'Deleting payment method',
            })
          );
          const paymentMethodDeleteResponse = await dispatch(
            deleteSupplierCustomerPaymentMethod({
              supplierId: currentSupplier?.id || '',
              customerId: selectedCustomer.buyer.id,
              paymentMethodId,
            })
          );
          if (
            paymentMethodDeleteResponse.type ===
            'payments/deleteSupplierCustomerPaymentMethod/fulfilled'
          ) {
            dispatch(
              openMessageModal({
                buttonText: 'Close',
                modalType: 'SUCCESS',
                title: 'Payment method deleted',
                subTitle: `Payment method deleted successfully`,
                onClose: () => {
                  getSavedCards();
                },
              })
            );
          } else {
            let errorMessage = '';
            if (
              paymentMethodDeleteResponse.type ===
              'payments/deleteSupplierCustomerPaymentMethod/rejected'
            ) {
              errorMessage = paymentMethodDeleteResponse.payload as any;
            }
            console.log({ errorMessage });
            dispatch(
              openMessageModal({
                buttonText: 'Close',
                modalType: 'ERROR',
                title: 'Error deleting payment method',
                subTitle: `${errorMessage} Contact (628) 304-0931 for customer support`,
              })
            );
          }
        },
      })
    );
  };

  const handleFiservCardResult = (result: FiservCardResult) => {
    if (result.success) {
      setVerifiedFiservCardData(result.data);
    } else {
      setVerifiedFiservCardData(null);
    }
  };

  return (
    <DillModal
      open={open}
      onClose={handleClose}
      title={title}
      maxWidth="640px"
      // contentClassName="flex flex-col gap-4"
      footer={
        <div className="flex justify-end gap-2">
          <DillButton label="Close" variant="secondary" onClick={handleClose} />
          <DillButton
            label="Pay"
            onClick={
              currentSupplier?.cardPaymentProvider === 'FISERV' ? handleFiservPay : handlePay
            }
          />
        </div>
      }>
      <div className="w-full">
        <div className="my-2 flex">
          <div className="w-full">
            <AppDropDown2
              isRequired
              label="Customer"
              errorText={isShowErrors && !selectedCustomer ? 'Required' : ''}
              value={selectedCustomer?.buyer?.id || ''}
              items={[
                ...fetchedSupplierBuyers
                  .filter((customerDetails) => !customerDetails?.buyer?.isDeactivated)
                  .map((customer) => {
                    return {
                      label: customer.buyer.name,
                      value: customer.buyer.id,
                      labelInElement: (
                        <div className="flex px-2 py-3 m-[-5px] items-center justify-between group relative">
                          <div className="text-sm">{`${customer.buyer.name}`}</div>
                        </div>
                      ),
                    };
                  }),
              ]}
              onSelectChange={(item) => {
                if (item && item?.value !== null) {
                  const foundSupplierBuyer = customers.find(
                    (supBuy) => supBuy.buyer.id === item?.value
                  );
                  if (foundSupplierBuyer) {
                    setSelectedCustomer(foundSupplierBuyer);
                    setSelectedCard('NEW_CARD');
                  }
                }
              }}
            />
          </div>
        </div>

        <div className="w-full my-2">
          <AppInputField
            prefix="$"
            label="Amount"
            isRequired={true}
            inputType="number"
            errorText={
              isShowErrors && hasBadAmountInput
                ? 'Must be a valid number'
                : isShowErrors && (paymentAmount === null || paymentAmount === undefined)
                ? 'Required'
                : isShowErrors && paymentAmount != null && paymentAmount <= 0
                ? 'Amount has to be greater than 0'
                : ''
            }
            value={paymentAmount ?? ''}
            onBlur={(e) => {
              if (e.target.validity?.badInput) {
                setHasBadAmountInput(true);
                setPaymentAmount(null);
              } else {
                setHasBadAmountInput(false);
                const val = e.target.value;
                if (!val) {
                  setPaymentAmount(null);
                } else {
                  setPaymentAmount(+Number(val).toFixed(2));
                }
              }
            }}
            onTextChange={(text) => {
              if (text) {
                setHasBadAmountInput(false);
                setPaymentAmount(Number(text));
              } else {
                setPaymentAmount(null);
              }
            }}
            onPaste={(e) => {
              const raw = e.clipboardData.getData('text');
              const cleaned = raw.replace(/[^0-9.]/g, '');
              const parsed = parseFloat(cleaned);
              if (!isNaN(parsed)) {
                e.preventDefault();
                setHasBadAmountInput(false);
                setPaymentAmount(+parsed.toFixed(2));
              }
            }}
          />
          <p className="text-xs font-medium text-slate-500 mt-2">
            Set the fees for credit card payments only. This will not apply to debit cards, as
            surcharging the customer on debit cards is not permitted according to federal law.
          </p>

          <div className="flex flex-col gap-2 bg-slate-50 p-4 rounded-lg">
            <div
              className="flex items-center cursor-pointer w-full my-0.5"
              onClick={() => {
                setWhoPaysFee('SUPPLIER');
                setBuyerTakeRate(0);
              }}>
              <AppRadioButton isChecked={whoPaysFee === 'SUPPLIER'} />
              <p className="text-xs ml-1 font-medium">Fees paid by supplier</p>
            </div>

            <div
              className={twMerge(
                'flex items-center cursor-pointer w-full my-0.5',
                (selectedCard &&
                  selectedCard !== 'NEW_CARD' &&
                  selectedCard.cardType === 'CREDIT') ||
                  (selectedCard === 'NEW_CARD' && paymentType === 'creditCard')
                  ? 'opacity-100 cursor-pointer'
                  : 'opacity-40'
              )}
              onClick={() => {
                if (
                  (selectedCard &&
                    selectedCard !== 'NEW_CARD' &&
                    selectedCard.cardType === 'CREDIT') ||
                  (selectedCard === 'NEW_CARD' && paymentType === 'creditCard')
                ) {
                  setPaymentType('creditCard');
                  setWhoPaysFee('BOTH');
                  if (currentSupplier?.cardPaymentProvider === 'FISERV') {
                    setBuyerTakeRate(
                      getBuyerFiservFeeRate({
                        buyer: selectedCustomer?.buyer,
                        supplier: currentSupplier,
                      })
                    );
                  } else {
                    setBuyerTakeRate(supplierTakeRate / 2);
                  }
                }
              }}>
              <AppRadioButton isChecked={whoPaysFee === 'BOTH' || whoPaysFee === 'BUYER'} />
              <p className="text-xs ml-1 font-medium">Fees paid by contractor</p>
            </div>
          </div>

          {(whoPaysFee === 'BOTH' || whoPaysFee === 'BUYER') && (
            <div className="flex flex-col mt-4 bg-slate-100 px-2 py-3 rounded-lg">
              <p className="text-xs font-bold ">
                Set how much of the transaction fees rate are paid by the contractor
              </p>

              <AppRangeSlider
                key={`slider-${whoPaysFee}-${supplierTakeRate}`}
                min={0}
                max={supplierTakeRate}
                valueUnits="%"
                valueLabel="Contractor fee rate"
                otherValueLabel={
                  currentSupplier?.cardPaymentProvider === 'FISERV' ? '' : 'Your fee rate'
                }
                value={buyerTakeRate}
                onChange={({ value }) => {
                  setBuyerTakeRate(value);
                  if (paymentType !== 'creditCard') {
                    setPaymentType('creditCard');
                  }
                }}
              />
            </div>
          )}
        </div>

        <div className="w-full my-2">
          <AppInputField
            label="Notes"
            placeholder="Sales Order #, etc"
            value={notes}
            errorText={isShowErrors && !notes ? 'Required' : ''}
            inputType="textarea"
            isRequired={true}
            onTextChange={(text) => {
              setNotes(text);
            }}
          />
        </div>
        {paymentAmount && (
          <div className="flex flex-col justify-between w-full pb-2  p-3 mt-3  bg-slate-50 rounded-md">
            <div className="flex flex-row justify-between w-full pt-1 ">
              <div className="text-base font-bold TEXT_SECONDARY-CLR">Amount</div>
              <div className="text-black font-bold text-lg">{`${formatMoney(
                paymentAmount,
                2
              )}`}</div>
            </div>
            {buyerFee > 0 && (
              <div className="flex flex-row justify-between w-full pt-1">
                <div className="text-base font-bold TEXT_SECONDARY-CLR">Fees</div>
                <div className="text-black font-bold text-lg">{`${formatMoney(buyerFee, 2)}`}</div>
              </div>
            )}

            <div className="flex flex-row justify-between w-full pt-1  border-t">
              <div className="text-base font-bold">Total Payment</div>
              <div className="PRIMARY_500-CLR font-bold text-lg">{`${formatMoney(
                paymentAmount + buyerFee,
                2
              )}`}</div>
            </div>
          </div>
        )}
        <div className="mt-6 mb-2">
          {isFetchingSaveCards && (
            <div className="flex items-center">
              <Spinner className="animate-spin" color="#3762FB" size={24} />
              <p className="ml-2 text-sm">Fetching cards</p>
            </div>
          )}
          {!isFetchingSaveCards && (
            <AppDropDown2
              isRequired
              label="Payment method"
              value={selectedCard === 'NEW_CARD' ? 'NEW_CARD' : selectedCard.id || ''}
              isCreatable={false}
              items={[
                ...[
                  {
                    id: 'NEW_CARD',
                    paymentMethodName: 'Enter Card Manually',
                    user: null,
                    expiry: '',
                  },
                  ...savedCards.filter((cardDetails) => {
                    if (
                      cardDetails.provider === 'FISERV' &&
                      currentSupplier?.cardPaymentProvider === 'FISERV'
                    ) {
                      return true;
                    }
                    if (
                      cardDetails.provider === 'STRIPE' &&
                      (currentSupplier?.cardPaymentProvider === 'STRIPE' ||
                        !currentSupplier?.cardPaymentProvider)
                    ) {
                      return true;
                    }
                    return false;
                  }),
                ].map((card) => {
                  return {
                    label: `${card.paymentMethodName || ''} ${card.user?.email || ''}`,
                    value: card.id,
                    labelInElement: (
                      <div className="flex px-2 py-3 m-[-5px] items-center justify-between group relative">
                        <div className="text-sm">
                          <p className="font-medium">{`${card.paymentMethodName}`}</p>
                          {card?.expiry && (
                            <p className="text-xs mr-1">{`Expires ${moment(
                              card.expiry,
                              card.expiry.length === 4 ? 'MMYY' : 'YYYYM'
                            ).format('MM/YY')}`}</p>
                          )}
                        </div>
                        {card.user?.email && (
                          <div className="flex">
                            <p className="text-xs mr-1">{`${card.user?.email}`}</p>
                            {card.id !== 'NEW_CARD' && (
                              <Trash
                                color={COLORS.ERROR_500}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteCustomerPaymentMethod({ paymentMethodId: card.id });
                                }}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    ),
                  };
                }),
              ]}
              onSelectChange={(item) => {
                if (item.value === 'NEW_CARD') {
                  setSelectedCard('NEW_CARD');
                } else {
                  const cardCheck = savedCards.find((card) => item.value && card.id === item.value);
                  if (cardCheck) {
                    setEmail('');
                    setSelectedCard(cardCheck);
                    if (cardCheck.cardType !== 'CREDIT') {
                      setWhoPaysFee('SUPPLIER');
                      setBuyerTakeRate(0);
                    }
                  }
                }
              }}
            />
          )}
        </div>

        {selectedCard === 'NEW_CARD' && (
          <div className="">
            <div className="flex flex-col p-4 PRIMARY_50-BG mt-4">
              <p className="mb-3">Card Details</p>

              <p className="text-xs font-bold  mb-1">Select Card Type</p>
              <div className="">
                {paymentOptions.map((option, i) => {
                  return (
                    <div key={i + 'oo'} className="w-full  my-1">
                      <button
                        className={
                          'flex flex-row w-full rounded-xl WHITE-BG px-5 py-2 border  items-center cursor-pointer ' +
                          (option.id === paymentType ? 'PRIMARY_500-BORDER' : ' GREY_300-BORDER')
                        }
                        onClick={() => {
                          setPaymentType(option.id);
                        }}>
                        <div className="flex  items-center">
                          <AppRadioButton isChecked={option.id === paymentType} />
                          <div style={{ marginRight: '6px' }} className="payment-type-logo ml-3">
                            {option.name === 'Bank Transfer' ? (
                              <Bank size={26} style={{ display: 'flex', alignSelf: 'center' }} />
                            ) : (
                              <CreditCard
                                size={26}
                                style={{ display: 'flex', alignSelf: 'center' }}
                              />
                            )}
                          </div>
                        </div>
                        <div className="flex justify-between w-full items-center">
                          <div className="flex flex-col">
                            <p className="text-xs font-bold text-left">{option.name}</p>
                          </div>
                          {option.id === 'creditCard' && buyerTakeRate && buyerTakeRate > 0 ? (
                            <div className="flex-end">
                              <div className="text-sm TEXT_SECONDARY-CLR ">Fees</div>
                              <div className="text-xs PRIMARY_500-CLR">{`${buyerTakeRate}%`}</div>
                            </div>
                          ) : (
                            <></>
                          )}
                        </div>
                      </button>
                    </div>
                  );
                })}
              </div>
              {(currentSupplier?.cardPaymentProvider === 'STRIPE' ||
                !currentSupplier?.cardPaymentProvider) && (
                <div
                  className={twMerge(
                    'px-1 py-3 mt-3 bg-white rounded-lg',
                    isShowErrors && !cardStatus.isCardComplete ? 'border border-[#F04438]' : ''
                  )}>
                  <label className="text-xs mb-1" htmlFor="card-element">
                    <CardElement
                      id="card-element"
                      onChange={handleCardChange}
                      options={{ disableLink: true }}
                    />
                  </label>
                  {isShowErrors && !cardStatus.isCardComplete && (
                    <div className="flex flex-col">
                      <p className="text-[#F04438] text-2xs mt-3">
                        Make sure to enter the right card details.
                      </p>
                      <p className="text-[#F04438] text-2xs">{cardStatus?.errorMessage}</p>
                    </div>
                  )}
                </div>
              )}
              {currentSupplier?.cardPaymentProvider == 'FISERV' && (
                <div
                  className={twMerge(
                    'px-1 py-3 mt-3 bg-white rounded-lg',
                    isShowErrors && !verifiedFiservCardData ? 'border border-[#F04438]' : ''
                  )}>
                  <AppFiservCardInput
                    onResult={handleFiservCardResult}
                    isWarnCardToBeSaved={isSaveCard}
                  />
                  {isShowErrors && !verifiedFiservCardData && (
                    <div className="flex flex-col">
                      <p className="text-[#F04438] text-2xs mt-3">
                        Make sure to enter the right card details.
                      </p>
                      <p className="text-[#F04438] text-2xs">{cardStatus?.errorMessage}</p>
                    </div>
                  )}
                </div>
              )}
              <div className="flex flex-col border-t pt-2">
                <div className="w-full">
                  <AppDropDown2
                    isRequired
                    label="Email"
                    errorText={
                      isShowErrors && !email
                        ? 'Required'
                        : isShowErrors && !/[\w.+-]+@[\w-]+\.[a-z]{1,}/gi.test(email)
                        ? 'Invalid email'
                        : ''
                    }
                    value={email || ''}
                    isCreatable={true}
                    validateNewItem={(newValue) =>
                      /[\w.+-]+@[\w-]+\.[a-z]{1,}/gi.test(newValue) ? undefined : 'Invalid email'
                    }
                    onCreateNewItem={(newValue) => {
                      setCustomerEmails([
                        ...(selectedCustomer?.usersEmailsDetails.map((email1) => email1.email) ||
                          []),
                        newValue,
                      ]);
                      setEmail(newValue);
                    }}
                    items={[
                      ...customerEmails
                        .filter((emailDetails) => /[\w.+-]+@[\w-]+\.[a-z]{1,}/gi.test(emailDetails))
                        .map((emailDetails) => {
                          return {
                            label: emailDetails,
                            value: emailDetails,
                            labelInElement: (
                              <div className="flex px-2 py-3 m-[-5px] items-center justify-between group relative">
                                <div className="text-sm">{`${emailDetails}`}</div>
                              </div>
                            ),
                          };
                        }),
                    ]}
                    onSelectChange={(item) => {
                      setEmail(item.value);
                    }}
                  />
                </div>
                <div
                  className="flex cursor-pointer items-center mt-2"
                  onClick={() => {
                    setIsSaveCard(!isSaveCard);
                  }}>
                  <DillCheckbox isChecked={isSaveCard} />
                  <p className="ml-2 text-xs font-semibold">Save card for future payments</p>
                </div>
              </div>
            </div>
          </div>
        )}
        {selectedCard !== 'NEW_CARD' && (
          <div
            className={twMerge(
              'flex flex-row rounded-xl  px-5 py-4 border justify-between items-center cursor-pointer PRIMARY_500-BORDER'
            )}>
            <div className="flex flex-row ">
              <div className="flex mr-2 items-center">
                <div className="payment-type-logo">
                  <CreditCard size={30} style={{ display: 'flex', alignSelf: 'center' }} />
                </div>
              </div>
              <div className="flex flex-col">
                <div className="text-sm font-bold text-left">
                  {selectedCard?.cardType
                    ? `${
                        selectedCard?.cardType === 'CREDIT' ? 'Credit Card' : 'Debit / Other Card'
                      }`
                    : 'Credit/Debit Card'}
                </div>
                <p className="accountDetails">{`${selectedCard.paymentMethodName}`}</p>
                {selectedCard.user?.email && (
                  <p className="text-2xs  text-left">{selectedCard.user?.email}</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </DillModal>
  );
};
